import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  LwHubCard,
  LwPageChrome,
  LwSearchInput,
  LwThemeScope
} from '@/components/lw';
import { getCompanySettingsHubModel } from './companySettingsHubModel';
import './company-settings-hub.css';

interface CompanySettingsHubProps {
  hasAnyPermission: (permissions: string[]) => boolean;
  onNavigate: (sectionId: string) => void;
}

function normalizeQuery(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function formatCatalogCount(
  count: number,
  singular: string,
  plural: string,
  capitalize = false
): string {
  const knownWords: Record<number, string> = {
    1: 'one',
    5: 'five',
    13: 'thirteen'
  };
  const rawCount = knownWords[count] ?? String(count);
  const displayCount = capitalize
    ? `${rawCount.charAt(0).toLocaleUpperCase()}${rawCount.slice(1)}`
    : rawCount;
  return `${displayCount} ${count === 1 ? singular : plural}`;
}

export function CompanySettingsHub({
  hasAnyPermission,
  onNavigate
}: CompanySettingsHubProps) {
  const [query, setQuery] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  const groups = useMemo(
    () => getCompanySettingsHubModel(hasAnyPermission),
    [hasAnyPermission]
  );
  const cardCount = groups.reduce((total, group) => total + group.cards.length, 0);
  const catalogDescription = `${formatCatalogCount(cardCount, 'tool', 'tools', true)}, ${formatCatalogCount(groups.length, 'family', 'families')}. Find the right place to manage company operations.`;
  const normalizedQuery = normalizeQuery(query);
  const visibleGroups = useMemo(() => groups.flatMap((group) => {
    const cards = normalizedQuery.length === 0
      ? group.cards
      : group.cards.filter((card) => (
        `${card.title} ${card.description} ${card.keywords}`
          .toLocaleLowerCase()
          .includes(normalizedQuery)
      ));
    return cards.length > 0 ? [{ ...group, cards }] : [];
  }), [groups, normalizedQuery]);

  useLayoutEffect(() => {
    const pageRoot = contentRef.current?.parentElement;
    const shellScroller = pageRoot?.parentElement;
    if (shellScroller) {
      shellScroller.scrollTop = 0;
    }
  }, []);

  return (
    <LwThemeScope className="lw-ds-page lw-company-settings-hub">
      <div className="wrap" ref={contentRef}>
        <LwPageChrome
          eyebrow="LendWise Administration"
          title={<>Company <em>Settings</em></>}
          description={catalogDescription}
          actions={(
            <div className="lw-company-settings-hub__search">
              <LwSearchInput
                value={query}
                onChange={setQuery}
                ariaLabel="Quick find Company Settings tools"
                placeholder="Quick find — try “comp” or “email”"
              />
            </div>
          )}
        />

        {visibleGroups.length > 0 ? (
          <div className="lw-company-settings-hub__groups">
            {visibleGroups.map((group) => {
              const headingId = `company-settings-group-${group.id}`;
              return (
                <section
                  className="lw-company-settings-hub__group"
                  key={group.id}
                  aria-labelledby={headingId}
                >
                  <div className="lw-company-settings-hub__group-heading">
                    <h2 id={headingId}>{group.title}</h2>
                    <p>{group.description}</p>
                    <span aria-hidden="true" />
                  </div>
                  <nav className="lw-company-settings-hub__grid" aria-labelledby={headingId}>
                    {group.cards.map((card) => {
                      const Icon = card.icon;
                      return (
                        <LwHubCard
                          key={card.id}
                          className={`lw-company-settings-hub__card lw-company-settings-hub__card--${card.frequency.toLocaleLowerCase()}`}
                          name={card.title}
                          description={card.description}
                          frequency={card.frequency}
                          icon={<Icon />}
                          aria-label={`Open ${card.title}`}
                          onClick={() => onNavigate(card.id)}
                        />
                      );
                    })}
                  </nav>
                </section>
              );
            })}
          </div>
        ) : (
          <div className="lw-company-settings-hub__empty" role="status">
            <h2>Nothing called “{query.trim()}”</h2>
            <p>Try “users”, “fees”, “email” — or clear the search.</p>
            <button type="button" className="btn btn-secondary" onClick={() => setQuery('')}>
              Clear search
            </button>
          </div>
        )}
      </div>
    </LwThemeScope>
  );
}

export default CompanySettingsHub;
